<template>
  <div class="text-editor-panel">
    <div class="editor-header">
      <div class="header-left">
        <i class="el-icon-document"></i>
        <span class="dataset-name">{{ datasetName }}</span>
        <el-tag type="success" size="small" style="margin-left: 12px;">
          <i class="el-icon-user"></i> 所属: {{ ownerName }}
        </el-tag>
      </div>
      <div class="header-right">
        <el-tag v-if="isEditing" type="primary" size="small">
          <i class="el-icon-edit"></i> 编辑模式
        </el-tag>
        <el-tag v-else type="info" size="small">
          <i class="el-icon-view"></i> 查看模式
        </el-tag>
      </div>
    </div>

    <div v-loading="loading" class="text-content-wrapper">
      <!-- 编辑模式 -->
      <div v-if="isEditing" class="edit-mode">
        <el-input
          type="textarea"
          v-model="editContent"
          :autosize="{ minRows: 15, maxRows: 25 }"
          placeholder="请输入文本内容..."
          class="text-editor">
        </el-input>
        
        <div class="edit-actions">
          <el-button 
            type="primary" 
            size="medium"
            icon="el-icon-check"
            @click="handleSave"
            :loading="saving">
            保存
          </el-button>
          <el-button 
            size="medium"
            icon="el-icon-close"
            @click="handleCancelEdit">
            取消
          </el-button>
        </div>
      </div>
      
      <!-- 查看模式 -->
      <div v-else class="view-mode">
        <div 
          class="text-display" 
          v-html="displayContent || '<p style=\'color: #909399;\'>暂无内容</p>'">
        </div>
        
        <div class="view-actions">
          <el-button 
            type="primary" 
            size="medium"
            icon="el-icon-edit"
            @click="handleStartEdit">
            修改
          </el-button>
          <el-button 
            size="medium"
            icon="el-icon-refresh"
            @click="loadData">
            刷新
          </el-button>
        </div>
      </div>
    </div>

    <div class="editor-footer">
      <div class="footer-left">
        <span v-if="textInfo.version" class="version-info">
          版本: {{ textInfo.version }}
        </span>
      </div>
      <div class="footer-right">
        <span v-if="textInfo.updateTime" class="update-time">
          最后更新: {{ textInfo.updateTime }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { getDataDetailById, updateTXT, lockData } from '@/api'

export default {
  name: 'DatasetTextEditor',
  
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    databaseId: {
      type: [Number, String, null],
      default: null
    },
    datasetName: {
      type: String,
      default: '未命名文本'
    },
    userOperatorId: {
      type: String,
      required: true
    },
    isPanelMode: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      loading: false,
      saving: false,
      isEditing: false,
      displayContent: '',
      editContent: '',
      originalContent: '',
      ownerName: '未知',  // 数据集拥有者名称
      ownerId: '',         // 数据集拥有者ID
      textInfo: {
        id: null,
        version: null,
        updateTime: null
      }
    }
  },
  
  watch: {
    visible(val) {
      if (!this.isPanelMode && val) {
        this.loadData()
      }
    },
    
    databaseId(val) {
      if (this.isPanelMode && val) {
        this.loadData()
      }
    }
  },
  
  mounted() {
    if (this.isPanelMode && this.databaseId) {
      this.loadData()
    }
  },
  
  methods: {
    /**
     * 加载数据
     */
    async loadData() {
      // 如果 databaseId 为空，不执行加载
      if (!this.databaseId) {
        return
      }
      
      try {
        this.loading = true
        const response = await getDataDetailById({
          database_id: this.databaseId,
          user_id: this.userOperatorId
        })
        
        if (response.code === 200 && response.data) {
          const { value, isTxt, ownerName, ownerId } = response.data
          
          // 保存拥有者信息
          this.ownerName = ownerName || '未知'
          this.ownerId = ownerId || ''
          
          if (isTxt === 1 && value && value.length > 0) {
            const textData = value[0]
            this.textInfo = {
              id: textData.id,
              version: textData.version,
              updateTime: textData.updateTime || null
            }
            
            // 提取文本内容
            this.displayContent = textData.content || ''
            this.originalContent = this.displayContent
          } else {
            this.$message.warning('该数据集不是文本类型')
          }
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error(error.message || '加载数据失败')
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 开始编辑
     */
    async handleStartEdit() {
      if (!this.textInfo.id) {
        this.$message.error('文本数据不存在')
        return
      }
      
      try {
        // 尝试锁定数据
        const lockResponse = await lockData({
          value_id: this.textInfo.id,
          lock_user_id: this.userOperatorId
        })
        
        if (lockResponse.code === 0 && lockResponse.data && lockResponse.data.lock) {
          // 锁定成功
          this.isEditing = true
          this.editContent = this.displayContent
          this.lockedByOtherUser = false
          this.lockedByUserName = ''
          this.$message.success('进入编辑模式')
        } else {
          // 锁定失败，数据已被其他用户锁定
          this.lockedByOtherUser = true
          this.lockedByUserName = lockResponse.data?.lockMessage || '其他用户'
          const lockMessage = lockResponse.data?.lockMessage || '该文本正在被其他用户编辑'
          this.$message.error(lockMessage)
        }
      } catch (error) {
        console.error('锁定数据失败:', error)
        this.lockedByOtherUser = true
        this.lockedByUserName = '其他用户'
        this.$message.error(error.message || '锁定数据失败')
      }
    },
    
    /**
     * 保存
     */
    async handleSave() {
      if (!this.textInfo.id) {
        this.$message.error('文本数据不存在')
        return
      }
      
      // 检查内容是否改变
      if (this.editContent === this.originalContent) {
        this.$message.info('内容未改变')
        this.handleCancelEdit(true)
        return
      }
      
      try {
        this.saving = true
        
        // 调用更新接口
        const response = await updateTXT({
          value_id: this.textInfo.id,
          user_operator_id: this.userOperatorId,
          value: this.editContent
        })
        
        if (response.code === 0) {
          this.displayContent = this.editContent
          this.originalContent = this.editContent
          this.textInfo.version += 1
          this.isEditing = false
          
          this.$message.success('保存成功')
          this.$emit('data-updated')
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error(error.message || '保存失败')
      } finally {
        this.saving = false
      }
    },
    
    /**
     * 取消编辑
     */
    async handleCancelEdit(silent = false) {
      if (!silent && this.editContent !== this.originalContent) {
        try {
          await this.$confirm('有未保存的更改，确定要取消吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '继续编辑',
            type: 'warning'
          })
        } catch {
          return
        }
      }
      
      // 注意：取消编辑时，锁会在一定时间后自动释放，不需要手动解锁
      
      this.isEditing = false
      this.editContent = ''
      
      if (!silent) {
        this.$message.info('已取消编辑')
      }
    },
    
    /**
     * 关闭对话框
     */
    handleClose() {
      if (this.isEditing) {
        this.$confirm('正在编辑中，确定要关闭吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.handleCancelEdit(true)
          if (this.isPanelMode) {
            this.$emit('close')
          } else {
            this.$emit('update:visible', false)
          }
        }).catch(() => {})
      } else {
        if (this.isPanelMode) {
          this.$emit('close')
        } else {
          this.$emit('update:visible', false)
        }
      }
    }
  }
}
</script>

<style scoped>
/* 面板模式样式 */
.text-editor-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow: hidden;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
}

.text-editor-dialog {
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #EBEEF5;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-left i {
  font-size: 20px;
  color: #409EFF;
}

.dataset-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.text-content-wrapper {
  min-height: 400px;
  margin: 16px 0;
}

/* 编辑模式样式 */
.edit-mode {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.text-editor {
  font-size: 14px;
}

.text-editor >>> textarea {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  line-height: 1.8;
  padding: 12px;
}

.edit-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

/* 查看模式样式 */
.view-mode {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.text-display {
  min-height: 400px;
  padding: 20px;
  background-color: #F5F7FA;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.8;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-y: auto;
  max-height: 600px;
}

.text-display >>> p {
  margin: 0 0 12px 0;
}

.text-display >>> p:last-child {
  margin-bottom: 0;
}

.view-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

/* 底部信息 */
.editor-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #EBEEF5;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.version-info {
  font-size: 12px;
  color: #606266;
  padding: 4px 8px;
  background-color: #F4F4F5;
  border-radius: 4px;
}

.footer-right {
  display: flex;
  align-items: center;
}

.update-time {
  font-size: 12px;
  color: #909399;
}

/* 对话框样式 */
.text-editor-dialog >>> .el-dialog__body {
  padding: 20px;
  max-height: calc(90vh - 180px);
  overflow-y: auto;
}

.text-editor-dialog >>> .el-dialog__header {
  border-bottom: 1px solid #EBEEF5;
  padding-bottom: 16px;
}

.text-editor-dialog >>> .el-dialog__footer {
  border-top: 1px solid #EBEEF5;
  padding-top: 16px;
}
</style>

